import i18n from 'utils/strings/i18n';
import { ApiError, apiUtils } from 'services/utils/apiUtils';

export interface UploadedVideoFiles {
  url?: string;
  captionsUrl?: string;
}

export interface VideoUploadFiles {
  video?: File;
  captions?: File;
}

const HttpBadRequest = 400;
const HttpUnauthorized = 401;
const HttpForbidden = 403;
const HttpPayloadTooLarge = 413;
const HttpUnsupportedMediaType = 415;
const HttpInsufficientStorage = 507;

/**
 * Uploads a video file, a captions file, or both, to the api. At least one of the two is required.
 * Rejects with an ApiError carrying the status and the body.
 */
export const uploadVideoFiles = (
  { video, captions }: VideoUploadFiles,
  onProgress?: (percent: number) => void
): Promise<UploadedVideoFiles> => {
  if (!video && !captions) {
    return Promise.reject(new ApiError(HttpBadRequest));
  }

  const formData = new FormData();
  if (video) {
    formData.append('video', video);
  }
  if (captions) {
    formData.append('captions', captions);
  }

  return apiUtils.apiUpload<UploadedVideoFiles>(`${process.env.REACT_APP_API_URL}/videos/upload`, formData, onProgress);
};

/**
 * Turns an upload rejection into a message shown to the admin.
 */
export const getUploadErrorMessage = (error: unknown): string => {
  const t = (key: string) => i18n.t(key, { ns: 'videoForm' });

  if (!(error instanceof ApiError)) {
    return t('uploadErrorGeneric');
  }

  switch (error.status) {
    case HttpPayloadTooLarge:
      return t('uploadErrorTooLarge');
    case HttpUnsupportedMediaType:
      return t('uploadErrorWrongType');
    case HttpInsufficientStorage:
      return t('uploadErrorNoSpace');
    case HttpBadRequest: {
      // The api answers with a Nest error body, whose message is a string or an array of strings.
      const message = (error.body as { message?: unknown } | undefined)?.message;
      const detail = Array.isArray(message) ? message.join(' ') : message;
      return typeof detail === 'string' && detail.length > 0 ? detail : t('uploadErrorInvalid');
    }
    case HttpUnauthorized:
    case HttpForbidden:
      return t('uploadErrorUnauthorized');
    default:
      return t('uploadErrorGeneric');
  }
};
